import { useState, useEffect } from 'react';
import { discountService, FeaturedProductsPricing, ProductPricing } from '@/services/discount.service';

/**
 * Hook to fetch pricing for all featured homepage products in a single API call.
 *
 * This is the SINGLE SOURCE OF TRUTH for homepage pricing.
 * All homepage cards MUST use this hook to ensure pricing consistency.
 *
 * @example
 * ```tsx
 * const { pricing, loading, isSaleActive } = useFeaturedPricing();
 *
 * if (loading) return <Skeleton />;
 *
 * return (
 *   <PriceDisplay
 *     displayPrice={pricing.prompt_engineering_mastery?.display_price}
 *     originalPrice={pricing.prompt_engineering_mastery?.original_price}
 *     showSale={pricing.prompt_engineering_mastery?.is_sale_active}
 *   />
 * );
 * ```
 */
export function useFeaturedPricing() {
  const [pricing, setPricing] = useState<FeaturedProductsPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await discountService.getFeaturedProductsPricing();
        if (response.success && response.data) {
          setPricing(response.data);
        } else {
          setError(response.error || 'Failed to fetch pricing');
        }
      } catch (err) {
        setError('Failed to fetch pricing');
        console.error('Error fetching featured pricing:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  return {
    pricing,
    loading,
    error,
    isSaleActive: pricing?.is_sale_active ?? false,
    systemDiscountCode: pricing?.system_discount_code ?? null,

    // Convenience accessors for specific products
    promptEngineeringPricing: pricing?.prompt_engineering_mastery ?? null,
    aiToolsPricing: pricing?.ai_tools_accelerator ?? null,
    bundlePricing: pricing?.ai_essentials_bundle ?? null,
  };
}

/**
 * Hook to fetch pricing for a single product.
 * Use this for individual product pages.
 *
 * @param productId - Product ID or slug
 * @param productType - 'course', 'bundle', or 'digital_product'
 */
export function useProductPricing(productId: string, productType: 'course' | 'bundle' | 'digital_product') {
  const [pricing, setPricing] = useState<ProductPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPricing = async () => {
      if (!productId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await discountService.getProductPricing(productId, productType);
        if (response.success && response.data) {
          setPricing(response.data);
        } else {
          setError(response.error || 'Failed to fetch pricing');
        }
      } catch (err) {
        setError('Failed to fetch pricing');
        console.error('Error fetching product pricing:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, [productId, productType]);

  return {
    pricing,
    loading,
    error,

    // Convenience accessors
    displayPrice: pricing?.display_price ?? 0,
    originalPrice: pricing?.original_price ?? 0,
    isSaleActive: pricing?.is_sale_active ?? false,
    savingsAmount: pricing?.savings_amount ?? 0,
    savingsPercentage: pricing?.savings_percentage ?? 0,
    systemDiscountCode: pricing?.system_discount_code ?? null,
  };
}

/**
 * Utility function to format price for display
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

/**
 * Utility function to format savings for display
 */
export function formatSavings(amount: number, percentage: number): string {
  if (amount <= 0) return '';
  return `Save ${formatPrice(amount)} (${percentage}% OFF)`;
}

export default useFeaturedPricing;
